import React, { useMemo } from 'react';
import { get } from 'lodash';
import { useLocalStorage } from 'usehooks-ts';
import { NavLink } from 'react-router-dom';
import { CustomItem, CustomItemComponentProps, CSSFn } from '@atlaskit/menu';
import { LeftSidebar as AtlaskitLeftSidebar } from '@atlaskit/page-layout';
import { SideNavigation, NavigationContent } from '@atlaskit/side-navigation';
import HomeIcon from '@atlaskit/icon/glyph/home';
import BillingIcon from '@atlaskit/icon/glyph/billing';
import PeopleGroupIcon from '@atlaskit/icon/glyph/people-group';
import CreditCardIcon from '@atlaskit/icon/glyph/creditcard';
import ListIcon from '@atlaskit/icon/glyph/list';
import MarketplaceIcon from '@atlaskit/icon/glyph/marketplace';
import FileIcon from '@atlaskit/icon/glyph/file';
import TableIcon from '@atlaskit/icon/glyph/table';
import { ENABLE_DESIGN_ITERATION_1, STATSIG_FEATURE_GATES, routePaths } from '@css-xp/ppc-constants';
import { getRouteUrl } from '@css-xp/ppc-utils';
// Directly importing TransactionAccountSwitcher from the package's index is causing issue.
import { TransactionAccountSwitcher } from '@css-xp/ppc-navigation/src/transaction-account-switcher';
import { useFeatureToggle } from '../../../hooks';
import { LEFT_SIDEBAR } from '../../../constants';
import { useApplicationContext } from '../../../common-context';
import { CollapsibleNavMenuWrapper } from './collapsible-nav-menu-wrapper';
import './left-sidebar.scss';

type CustomComponentWithHrefProps = CustomItemComponentProps & {
  href: string;
  exact: boolean;
  activeClassName: string;
};
const cssFn: CSSFn = () => {
  return {
    position: 'relative',
    overflow: 'hidden',
    userSelect: 'none',
    padding: '8px',
    margin: '0 8px',
    borderRadius: '3px',
    '::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      transform: 'translateX(-1px)',
      transition: 'transform 70ms ease-in-out',
    },

    ':hover::before': {
      transform: 'translateX(0)',
      width: 3,
    },
  };
};

const CustomComponent = ({ children, href, ...props }: CustomComponentWithHrefProps) => {
  return (
    // eslint-disable-next-line
    <NavLink to={href} {...props}>
      {children}
    </NavLink>
  );
};

export const LeftSidebar = () => {
  const userProfile = useApplicationContext();
  const [isEnabled] = useFeatureToggle();
  const [selectedPartnerAccount, setSelectedPartnerAccount] = useLocalStorage(
    `${userProfile?.atlassianAccountId}-selectedTxa`,
    ''
  );
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const isIULPhase2Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_IUL_PHASE_2);
  const isDealRegistrationsEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_DEAL_REGISTRATIONS);

  const partnerAccounts = get(userProfile, 'partnerAccounts', []);
  const subscriptionsText = 'Subscriptions';

  const navigationPrimaryItems = useMemo(() => {
    // testId kept same as in packages/components/partner-purchase-center/components/navigation/src/navigation-bar.tsx
    const navigations = [
      {
        name: 'Home',
        testId: 'home-nav',
        iconBefore: <HomeIcon label="Home" />,
        href: getRouteUrl(routePaths.home, { txaId: selectedPartnerAccount }),
        exact: true,
      },
      {
        name: 'Billing account',
        testId: 'billing-account-nav',
        iconBefore: <BillingIcon label="Billing account" />,
        href: getRouteUrl(routePaths.details, { txaId: selectedPartnerAccount }),
        exact: true,
      },
      {
        name: 'Customers',
        testId: 'address-nav',
        iconBefore: <PeopleGroupIcon label="Customers" />,
        href: getRouteUrl(routePaths.customerAddresses, { txaId: selectedPartnerAccount }),
        exact: !isDesignIteration1Enabled,
      },
      {
        name: 'Invoices',
        testId: 'invoices-nav',
        iconBefore: <CreditCardIcon label="Invoices" />,
        href: getRouteUrl(routePaths.invoices, { txaId: selectedPartnerAccount }),
        exact: !isDesignIteration1Enabled,
      },
      {
        name: subscriptionsText,
        testId: 'subscriptions-nav',
        iconBefore: <ListIcon label="Subscriptions" />,
        href: getRouteUrl(routePaths.subscriptions, { txaId: selectedPartnerAccount }),
        exact: false,
      },
      {
        name: 'Catalog',
        testId: 'catalog-nav',
        iconBefore: <MarketplaceIcon label="Catalog" />,
        href: getRouteUrl(routePaths.catalog, { txaId: selectedPartnerAccount }),
        exact: true,
      },
    ];

    navigations.push({
      name: 'Quotes',
      testId: 'quotes-nav',
      iconBefore: <FileIcon label="Quotes" />,
      href: getRouteUrl(routePaths.quotes, { txaId: selectedPartnerAccount }),
      exact: !isDesignIteration1Enabled,
    });

    if (isDealRegistrationsEnabled) {
      navigations.push({
        name: 'Deals',
        testId: 'deals-nav',
        iconBefore: <TableIcon label="Deals" />,
        href: getRouteUrl(routePaths.dealRegistrations, { txaId: selectedPartnerAccount }),
        exact: true,
      });
    }

    return navigations;
  }, [selectedPartnerAccount, isEnabled]);

  if (!isDesignIteration1Enabled) {
    return null;
  }

  const getNavigationPrimaryItems = () => {
    if (partnerAccounts.length > 0) {
      return navigationPrimaryItems.map((navItem) => {
        if (isIULPhase2Enabled && navItem.name === subscriptionsText) {
          const internalUsageSubscriptionsText = 'Internal usage subscriptions';
          const allSubscriptionsText = 'All subscriptions';

          return (
            <CollapsibleNavMenuWrapper key={navItem.testId}>
              <CustomItem
                href={getRouteUrl(routePaths.subscriptions, { txaId: selectedPartnerAccount })}
                key={allSubscriptionsText}
                iconBefore={<ListIcon label={allSubscriptionsText} />}
                exact={false}
                activeClassName="active"
                component={CustomComponent}
                cssFn={cssFn}
              >
                <span className="nav-item-text">{allSubscriptionsText}</span>
              </CustomItem>
              <CustomItem
                href={getRouteUrl(routePaths.internalUsageSubscriptions, { txaId: selectedPartnerAccount })}
                key={internalUsageSubscriptionsText}
                iconBefore={<ListIcon label={internalUsageSubscriptionsText} />}
                exact={false}
                activeClassName="active"
                component={CustomComponent}
                cssFn={cssFn}
              >
                <span className="nav-item-text">{internalUsageSubscriptionsText}</span>
              </CustomItem>
            </CollapsibleNavMenuWrapper>
          );
        }

        return (
          <CustomItem
            href={navItem.href}
            exact={navItem.exact}
            activeClassName="active"
            component={CustomComponent}
            cssFn={cssFn}
            key={navItem.testId}
            iconBefore={navItem.iconBefore}
          >
            <span className="nav-item-text">{navItem.name}</span>
          </CustomItem>
        );
      });
    }

    return null;
  };

  return (
    <AtlaskitLeftSidebar id="left-sidebar-container" width={LEFT_SIDEBAR.WIDTH} testId="left-sidebar-container">
      <SideNavigation label="Left sidebar">
        <NavigationContent>
          <div className="txa-switcher-container">
            <TransactionAccountSwitcher
              partnerAccounts={partnerAccounts}
              selectedPartnerAccount={selectedPartnerAccount}
              setSelectedPartnerAccount={setSelectedPartnerAccount}
              hasSelectInterface={true}
            />
          </div>
          <div className="nav-items">{getNavigationPrimaryItems()}</div>
        </NavigationContent>
      </SideNavigation>
    </AtlaskitLeftSidebar>
  );
};
